import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  Component, computed, inject, signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TranslocoModule } from '@jsverse/transloco';

import { ButtonColors, ButtonComponent } from '../button/button.component';
import { ButtonCloseComponent } from '../button-close/button-close.component';
import { InlineSvgComponent } from '../inline-svg/inline-svg.component';

import { AlertPopUpData, AlertPopUpPromptData } from './alert-pop-up.service';

interface AlertPopUpType {
  icon: string;
  buttonColors: ButtonColors;
}

@Component({
  selector: 'ui-alert-pop-up',
  imports: [
    CommonModule,
    TranslocoModule,
    FormsModule,
    InlineSvgComponent,
    ButtonComponent,
    ButtonCloseComponent,
  ],
  templateUrl: './alert-pop-up.component.html',
  styleUrl: './alert-pop-up.component.scss',
  standalone: true,
})
export class AlertPopUpComponent {
  data = inject<AlertPopUpData | AlertPopUpPromptData>(DIALOG_DATA);

  title: string;
  titleParams?: Record<string, any>;
  subtitle?: string;
  subtitleParams?: Record<string, any>;
  label: string;
  labelParams?: Record<string, any>;

  // If we decide to support more then confirm and prompt, we need to change this!!
  isPrompt = 'prompt' in this.data;
  promptValue = signal<string>('');
  promptPlaceholder?: string;
  promptRequired = false;
  promptBlank = computed(() => this.promptRequired && !this.promptValue().trim());

  private dialogRef = inject(DialogRef);

  constructor() {
    const data = this.data;

    if (typeof data.title !== 'string') {
      this.title = data.title.key;
      this.titleParams = data.title.params;
    } else {
      this.title = data.title;
    }

    if (data.subtitle && typeof data.subtitle !== 'string') {
      this.subtitle = data.subtitle.key;
      this.subtitleParams = data.subtitle.params;
    } else {
      this.subtitle = data.subtitle;
    }

    if (data.label && typeof data.label !== 'string') {
      this.label = data.label.key;
      this.labelParams = data.label.params;
    } else {
      this.label = data.label;
    }

    if ('prompt' in data) {
      this.promptValue.set(data.prompt.initialValue || '');
      this.promptPlaceholder = data.prompt.placeholder;
      this.promptRequired = !!data.prompt.required;
    }
  }


  getTypeData(): AlertPopUpType {
    switch (this.data.type) {
      case 'default':
        return {
          icon: this.data.icon || 'sparkles',
          buttonColors: 'onLight',
        };
      case 'info':
        return {
          icon: this.data.icon || 'alert-circle',
          buttonColors: 'info',
        };
      case 'warning':
        return {
          icon: this.data.icon || 'alert-circle',
          buttonColors: 'warning',
        };
      case 'danger':
        return {
          icon: this.data.icon || 'alert-triangle',
          buttonColors: 'danger',
        };
      default:
        return {
          icon: this.data.icon || 'sparkles',
          buttonColors: 'onLight',
        };
    }
  }

  executeAction(index?: number): void {
    if (index === undefined) {
      this.dialogRef.close();

      return;
    }

    const result = index === 1;

    if (result && this.promptBlank()) {
      return;
    }

    if (this.isPrompt) {
      this.dialogRef.close(result ? this.promptValue() : null);

      return;
    }

    this.dialogRef.close(result);
  }
}
